// assets/AllCourses/Exercises/ListeningExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import { cn } from "@/utils/utils";

export default function ListeningExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false, // ✅ FIX (match parent)
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.listeningQuestions)
    ? exercise.listeningQuestions.map((q) => ({
        wordListening:
          typeof q.wordListening === "string"
            ? q.wordListening.trim()
            : "",
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {},
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setState({
      answers: {},
      hasInteracted: false,
    });

    completedRef.current = false;
  }, [exercise._id]);

  /* ================= RESET WHEN RESULT OFF ================= */
  useEffect(() => {
    if (!showResult) completedRef.current = false;
  }, [showResult]);

  /* ================= SPEECH CLEAN ================= */
  const sanitizeForSpeech = (text = "") => {
    return String(text)
      .replace(/_{2,}/g, " blank ")
      .replace(/_/g, " underscore ")
      .replace(/\s+/g, " ")
      .trim();
  };

  /* ================= NORMALIZE ================= */
  const normalize = (text = "") =>
    String(text || "")
      .trim()
      .toLowerCase()
      .replace(/[.,!?]/g, "")
      .replace(/\s+/g, " ");

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("listeningQuestions", next);
  };

  const updateWord = (idx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[idx] = {
      wordListening:
        typeof value === "string" ? value.trim() : "",
    };

    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
  const handleInputChange = (idx, value) => {
    if (isEditMode || showResult) return;

    setState((prev) => {
      const answers = { ...prev.answers, [idx]: value };

      const next = {
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      // ✅ ALWAYS notify parent (IMPORTANT)
      onComplete(answers, false);

      const allAnswered = questions.every(
        (_, i) => answers[i]?.trim()
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every(
          (q, i) =>
            normalize(answers[i]) ===
            normalize(q.wordListening)
        );

        // ✅ FINAL COMPLETE
        onComplete(answers, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <StringField
                label={`Word / Sentence ${idx + 1}`}
                value={q.wordListening}
                onChange={(v) => updateWord(idx, v)}
                placeholder="apple / Good morning"
                required
                error={
                  errors?.[
                    `exercise-${exercise?.index ?? idx}-listening-${idx}`
                  ]
                }
              />

              {q.wordListening && (
                <div className="flex items-center gap-3">
                  <SpeakerButton
                    text={sanitizeForSpeech(q.wordListening)}
                  />
                  <span className="text-sm text-muted-foreground">
                    Preview pronunciation
                  </span>
                </div>
              )}

              <Button
                size="sm"
                variant="destructive"
                disabled={questions.length === 1}
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== idx)
                  )
                }
              >
                Remove
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              { wordListening: "" },
            ])
          }
        >
          Add Word / Sentence
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No listening questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Listening Practice
        </CardTitle>
        <CardDescription>
          Listen carefully and type exactly what you hear.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, idx) => {
          const userAnswer = state.answers[idx] || "";

          const isCorrect =
            userAnswer &&
            normalize(userAnswer) ===
              normalize(q.wordListening);

          return (
            <div
              key={idx}
              className="rounded-xl border bg-muted/40 p-6 space-y-4"
            >
              <div className="flex items-center gap-4">
                <SpeakerButton
                  text={sanitizeForSpeech(q.wordListening)}
                  size="lg"
                />

                <span className="text-sm text-muted-foreground">
                  Click to listen, then type below
                </span>
              </div>

              <Textarea
                rows={3}
                value={userAnswer}
                placeholder="Type what you hear..."
                onChange={(e) =>
                  handleInputChange(idx, e.target.value)
                }
                disabled={showResult}
                className={cn(
                  "text-base transition border",

                  // ✅ ONLY INPUT highlight (your requirement)
                  showResult &&
                    userAnswer &&
                    isCorrect &&
                    "bg-green-100 border-green-400",

                  showResult &&
                    userAnswer &&
                    !isCorrect &&
                    "bg-red-100 border-red-400",

                  showResult && "cursor-not-allowed opacity-95"
                )}
              />

              {showResult && userAnswer && (
                <p
                  className={cn(
                    "text-sm font-medium",
                    isCorrect
                      ? "text-green-600"
                      : "text-red-600"
                  )}
                >
                  {isCorrect
                    ? "Correct"
                    : `Correct answer: "${q.wordListening}"`}
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}